/**
 * banner
 * @returns ReactElement
 */

import { TextClamp } from '@/components/TextClamp';
import { Page } from '@/config/track-config';
import { ActivityStatus, activityInfo } from '@/types';
import { stringBrChange } from '@/utils/common';
import { Tooltip } from '@bfe/moly';
import {
  FollowersIcon,
  GlobeIcon,
  XIcon,
  GiftIcon,
  ShareIcon,
  HelpIcon,
} from '@bfe/moly-icons';
import { useFm, useParams } from '@web3/base-hooks';
import { formatKMB, isMobile, appPath } from '@web3/base-utils';
import cls from 'classnames';
import Link from 'next/link';

import styles from './index.module.scss';

type BannerProp = {
  data: activityInfo | undefined;
  pageLoading: boolean;
  handleShare: () => void;
};

export function Banner(props: BannerProp) {
  const { data, pageLoading } = props;
  const { locale } = useParams();
  const fm = useFm();
  return (
    <div
      className={cls([
        `${styles['banner-wrap']} relative`,
        isMobile && `${styles['banner-wrap-h5']} text-center`,
      ])}
    >
      <div
        className={cls(
          'main flex',
          isMobile ? 'mt-[10px] flex-col' : 'mt-16 gap-8'
        )}
      >
        <div className={cls(isMobile ? 'w-full' : 'flex-1')}>
          <div className="inline-flex items-center gap-2">
            <h1 className="">
              <span
                className="mr-2"
                dangerouslySetInnerHTML={{
                  __html: stringBrChange(
                    pageLoading ? '--' : data?.activityTitle || ''
                  ),
                }}
              ></span>
              {!isMobile && !pageLoading && (
                <>
                  <span
                    className={cls([
                      `${styles['status']} ${styles['status-progress']} relative -top-1`,
                      data?.activityStatus === ActivityStatus.upcoming
                        ? `${styles['status-upcoming']}`
                        : data?.activityStatus === ActivityStatus.progress
                          ? `${styles['status-progress']}`
                          : ' status-ended',
                    ])}
                  >
                    {data?.activityStatus === ActivityStatus.upcoming
                      ? fm('status_upcoming')
                      : data?.activityStatus === ActivityStatus.progress
                        ? fm('status_progress')
                        : fm('status_ended')}
                  </span>
                </>
              )}
            </h1>
          </div>
          <p
            className={cls([
              ' text-gray1 font-normal',
              isMobile ? 'mt-2 text-xs' : 'mt-2 text-sm',
            ])}
          >
            {pageLoading ? '--' : data?.activitySubtitle}
          </p>
          {!pageLoading && (
            <div
              className={cls([
                'flex gap-2 text-gray1 mt-2',
                isMobile ? ' justify-center text-xs' : 'text-sm',
              ])}
            >
              <div className="flex items-center gap-1">
                <FollowersIcon className={cls(isMobile && 'w-[14px] h-14px')} />
                <span>
                  {data?.participants === 0
                    ? '--'
                    : formatKMB(data?.participants as number, 2)}
                </span>
              </div>
              {data?.website && (
                <>
                  <div className={styles['separator']}></div>
                  <Link
                    target="_blank"
                    href={data?.website as string}
                    className="flex items-center gap-1 text-gray1"
                    data-spm-even="Web3UserClick"
                    data-spm-data={JSON.stringify({
                      page_name: Page.AirdropArcadeDetail,
                      button_name: 'Website',
                    })}
                  >
                    <GlobeIcon className={cls(isMobile && 'w-[14px] h-14px')} />
                    <span>{fm('website')}</span>
                  </Link>
                </>
              )}
              {data?.mediaUrl && (
                <>
                  <div className={styles['separator']}></div>
                  <Link
                    target="_blank"
                    href={data?.mediaUrl as string}
                    className="flex items-center gap-1 text-gray1"
                    data-spm-even="Web3UserClick"
                    data-spm-data={JSON.stringify({
                      page_name: Page.AirdropArcadeDetail,
                      button_name: 'Twitter',
                    })}
                  >
                    <XIcon className={cls(isMobile && 'w-[14px] h-14px')} />
                    <span>{fm('x')}</span>
                  </Link>
                </>
              )}
            </div>
          )}
          {/* 活动详情 */}
          <TextClamp
            line={2}
            text={pageLoading ? '--' : data?.activityDetail}
            className={cls([
              'text-gray1',
              isMobile ? ' text-xs mt-2' : ' text-base mt-6',
            ])}
          ></TextClamp>
        </div>
        <div className="content-end flex flex-col items-end max-w-[420px] min-w-[240px]">
          {!isMobile && (
            <div className="flex gap-4 justify-end">
              <div
                className="flex items-center gap-1 cursor-pointer hover:opacity-80 whitespace-nowrap"
                onClick={() => {
                  window.open(`/${locale}${appPath}/rewards`, '_blank');
                }}
                data-spm-even="Web3UserClick"
                data-spm-data={JSON.stringify({
                  page_name: Page.AirdropArcadeDetail,
                  button_name: 'My Rewards',
                })}
              >
                <GiftIcon />
                {fm('myRewards')}
              </div>
              <div
                className="flex items-center gap-1 cursor-pointer hover:opacity-80 whitespace-nowrap"
                onClick={props.handleShare}
                data-spm-even="Web3UserClick"
                data-spm-data={JSON.stringify({
                  page_name: Page.AirdropArcadeDetail,
                  button_name: 'Share',
                })}
              >
                <ShareIcon />
                {fm('share')}
              </div>
            </div>
          )}
          <div
            className={cls([
              `${styles['reward-card']} w-full`,
              isMobile
                ? `${styles['reward-card-h5']} mt-6 flex justify-between gap-2`
                : ' mt-9',
            ])}
          >
            <div className="flex gap-2 items-center">
              {fm('rewardsPool')}
              {data?.rewardPoolDetail && (
                <Tooltip title={data?.rewardPoolDetail} placement="top">
                  <HelpIcon className="text-gray cursor-pointer relative top-1 hover:opacity-80" />
                </Tooltip>
              )}
            </div>
            <h1
              className={cls([
                'font-semibold',
                isMobile ? 'text-lg text-right' : 'text-2xl  mt-2',
              ])}
            >
              <span className="line-clamp-2">
                {pageLoading ? '--' : data?.rewardDesc}
              </span>
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
}
